import React, { useRef, DragEvent, useState } from 'react'

import imageCompression from 'browser-image-compression'
import dayjs from 'dayjs'
import toast from 'react-hot-toast'
import { MdOutlineAddAPhoto } from 'react-icons/md'
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'

import { Button } from '..'
import { useStorage } from '../../context'
import { Spacer } from '../global'
import Modal from '../modal/Modal'
import Spinner from '../Spinner/Spinner'
import { SectionSubtitle } from '../texts'

import {
  DragAndDropContainer,
  EditImageModal,
  Image,
  ImageContainer,
} from './styles'

interface DragAndDropProps {
  type: string
  image?: string | null
  handleImageChange: (
    filterName: string,
    value: string | string[] | null,
  ) => void
  onEditionMode: boolean
  refId: string
  handleFileUpload: (refId: string, blob: Blob) => Promise<string>
  bgImage?: string
  isSquareCrop?: boolean
}

const DragAndDrop = ({
  type,
  image,
  handleImageChange,
  onEditionMode,
  refId,
  handleFileUpload,
  bgImage,
  isSquareCrop = false,
}: DragAndDropProps) => {
  const now = dayjs().format()
  //uploadProfileImage
  const { error, loadingStorage } = useStorage()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [crop, setCrop] = useState<Crop>()
  const [croppedImage, setCroppedImage] = useState<string | null>(null)
  const [imageRef, setImageRef] = useState<HTMLImageElement | null>(null)
  const [openEditionModal, setOpenEditionModal] = useState<boolean>(false)

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragging(true)
  }

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragging(false)
  }

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
  }

  const handleFiles = (file: File | undefined) => {
    if (file) {
      const reader = new FileReader()

      reader.onload = (e) => {
        if (e.target) {
          const imageSrc = e.target?.result as string
          setSelectedImage(imageSrc)
          setOpenEditionModal(true)
        }
      }

      reader.readAsDataURL(file)
    }
  }

  const handleImageLoaded = (image: HTMLImageElement) => {
    setImageRef(image)

    if (isSquareCrop) {
      const size = Math.min(image.width, image.height)
      setCrop({
        unit: 'px',
        x: (image.width - size) / 2,
        y: (image.height - size) / 2,
        width: size,
        height: size,
      })
    }
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragging(false)
    const file = e.dataTransfer.files?.[0]
    handleFiles(file)
  }

  const getCroppedImg = async (crop: PixelCrop) => {
    if (imageRef && crop.width && crop.height) {
      const canvas = document.createElement('canvas')
      const scaleX = imageRef.naturalWidth / imageRef.width
      const scaleY = imageRef.naturalHeight / imageRef.height
      canvas.width = crop.width
      canvas.height = crop.height
      const ctx = canvas.getContext('2d')

      if (ctx) {
        ctx.drawImage(
          imageRef,
          crop.x * scaleX,
          crop.y * scaleY,
          crop.width * scaleX,
          crop.height * scaleY,
          0,
          0,
          crop.width,
          crop.height,
        )

        const base64Image = canvas.toDataURL('image/jpeg')
        const compressedImage = await imageCompression.canvasToFile(
          canvas,
          'image/jpeg',
          now,
          0.7,
        )
        if (compressedImage.size > 500 * 1024) {
          const options = {
            maxSizeMB: 0.5,
            maxWidthOrHeight: 500,
            useWebWorker: true,
          }
          const compressedFile = await imageCompression(
            compressedImage,
            options,
          )
          const reader = new FileReader()
          reader.readAsDataURL(compressedFile)
          reader.onloadend = () => {
            setCroppedImage(reader.result as string)
          }
        } else {
          setCroppedImage(base64Image)
        }
      }
    }
  }

  const handleUpload = async () => {
    const imageToUpload = croppedImage || selectedImage

    if (imageToUpload) {
      const blob = await fetch(imageToUpload).then((res) => res.blob())

      try {
        const fileReference = await handleFileUpload(refId, blob)

        handleImageChange('image', fileReference)
        toast.success('Archivo guardado')
      } catch (error) {
        console.error('Error al subir el archivo:', error)
        toast.error('Hubo un error!')
      }
    }

    setOpenEditionModal(false)
    setSelectedImage(null)
    setCroppedImage(null)
    setImageRef(null)
  }

  return (
    <DragAndDropContainer
      className={`drop-zone ${dragging ? 'dragging' : ''}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {type === 'image' && (
        <ImageContainer $image={image || undefined} $onEdition={onEditionMode}>
          <p>
            <MdOutlineAddAPhoto /> <br />
            Arrastra una imagen <br />o haz clic para subir
          </p>
          <Image
            $image={image ? image : !onEditionMode ? bgImage : undefined}
            $onEdition={onEditionMode}
            onClick={handleButtonClick}
          >
            {onEditionMode && image && (
              <div className="overlay">
                <p>
                  <MdOutlineAddAPhoto /> <br />
                  Arrastra una imagen o haz clic para subir
                </p>
              </div>
            )}
          </Image>
        </ImageContainer>
      )}

      <input
        type="file"
        ref={onEditionMode ? fileInputRef : null}
        style={{ display: 'none' }}
        accept="image/*"
        onChange={(e) => handleFiles(e.target.files?.[0])}
      />

      {openEditionModal && selectedImage !== null && (
        <Modal onClose={() => setOpenEditionModal(false)}>
          {loadingStorage ? (
            <Spinner />
          ) : (
            <EditImageModal>
              <Spacer height={20} />
              {error ? (
                <SectionSubtitle> {error} </SectionSubtitle>
              ) : (
                <>
                  <SectionSubtitle fontSize={16} textAlign="center">
                    Quieres recortar la imagen?
                  </SectionSubtitle>
                  <Spacer height={20} />
                  <ReactCrop
                    crop={crop}
                    onChange={(newCrop) => setCrop(newCrop)}
                    onComplete={getCroppedImg}
                    aspect={isSquareCrop ? 1 : undefined}
                  >
                    <img
                      src={selectedImage}
                      alt="Cropped"
                      onLoad={(e) => handleImageLoaded(e.currentTarget)}
                    />
                  </ReactCrop>
                  <Spacer height={20} />

                  <Button
                    onClick={handleUpload}
                    fontSize={16}
                    className="light-grey"
                    label="Aceptar"
                  />
                </>
              )}
            </EditImageModal>
          )}
        </Modal>
      )}
    </DragAndDropContainer>
  )
}

export default DragAndDrop
